import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { errorLogger, ErrorCategory } from '../../services/errorLogger';
import { AppIcon } from '../icons';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onReset?: () => void;
    t: ReturnType<typeof useTranslation>['t'];
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorCategory: ErrorCategory | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorCategory: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorCategory: errorLogger.categorize(error)
        };
    }

    // Detect errors caused by stale JS chunks after a new deployment
    private static isChunkLoadError(error: Error): boolean {
        return (
            error.message?.includes('Failed to fetch dynamically imported module') ||
            error.message?.includes('ChunkLoadError') ||
            error.message?.includes('Loading chunk') ||
            error.message?.includes('error loading dynamically imported module') ||
            error.name === 'ChunkLoadError'
        );
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Auto-refresh on chunk load errors (new deployment invalidated old chunks).
        // sessionStorage flag prevents infinite reload loops.
        if (ErrorBoundary.isChunkLoadError(error)) {
            const REFRESH_KEY = 'chunk_error_refresh_ts';
            const lastRefresh = sessionStorage.getItem(REFRESH_KEY);
            const now = Date.now();
            if (!lastRefresh || now - parseInt(lastRefresh) > 30000) {
                sessionStorage.setItem(REFRESH_KEY, String(now));
                window.location.reload();
                return;
            }
        }

        // Log error with context
        errorLogger.log(error, {
            component: errorInfo.componentStack?.split('\n')[1]?.trim() || 'Unknown',
            page: typeof window !== 'undefined' ? window.location.pathname : undefined,
            additionalData: {
                componentStack: errorInfo.componentStack
            }
        });
    }

    private handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorCategory: null
        });

        if (this.props.onReset) {
            this.props.onReset();
        }
    };

    private handleGoToDashboard = () => {
        window.location.href = '/dashboard';
    };

    private handleReload = () => {
        window.location.reload();
    };

    private getErrorConfig() {
        const { errorCategory } = this.state;

        switch (errorCategory) {
            case 'network':
                return {
                    icon: 'wifi' as const,
                    title: this.props.t('errors.network_title'),
                    description: 'Impossibile connettersi al server. Controlla la tua connessione internet.',
                    iconColor: 'text-amber-500',
                    iconBg: 'bg-amber-50',
                    actions: [
                        { label: this.props.t('errors.retry'), onClick: this.handleReset, variant: 'primary' as const }
                    ]
                };

            case 'auth':
                return {
                    icon: 'lock' as const,
                    title: this.props.t('errors.session_expired_title'),
                    description: this.props.t('errors.session_expired_desc'),
                    iconColor: 'text-red-500',
                    iconBg: 'bg-red-50',
                    actions: [
                        { label: this.props.t('errors.go_to_login'), onClick: () => window.location.href = '/login', variant: 'primary' as const }
                    ]
                };

            case 'api':
                return {
                    icon: 'alert' as const,
                    title: this.props.t('errors.server_error_title'),
                    description: this.props.t('errors.server_error_desc'),
                    iconColor: 'text-orange-500',
                    iconBg: 'bg-orange-50',
                    actions: [
                        { label: this.props.t('errors.retry'), onClick: this.handleReset, variant: 'primary' as const },
                        { label: this.props.t('errors.go_to_dashboard'), onClick: this.handleGoToDashboard, variant: 'secondary' as const }
                    ]
                };

            default:
                return {
                    icon: 'alert' as const,
                    title: this.props.t('errors.unexpected_error_title'),
                    description: this.props.t('errors.unexpected_error_desc'),
                    iconColor: 'text-slate-500',
                    iconBg: 'bg-slate-50',
                    actions: [
                        { label: this.props.t('errors.reload_page'), onClick: this.handleReload, variant: 'primary' as const },
                        { label: this.props.t('errors.go_to_dashboard'), onClick: this.handleGoToDashboard, variant: 'secondary' as const }
                    ]
                };
        }
    }

    public render() {
        if (this.state.hasError) {
            // Use custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            const config = this.getErrorConfig();
            const isDev = import.meta.env.DEV;

            return (
                <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4">
                    <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                        {/* Header */}
                        <div className={`${config.iconBg} p-8 flex justify-center`}>
                            <div className={`w-20 h-20 rounded-full bg-white shadow-lg flex items-center justify-center ${config.iconColor}`}>
                                <AppIcon name={config.icon} className="w-10 h-10" />
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-8">
                            <h1 className="text-2xl font-bold text-slate-900 mb-3 text-center font-title">
                                {config.title}
                            </h1>
                            <p className="text-slate-600 mb-6 text-center leading-relaxed">
                                {config.description}
                            </p>

                            {/* Error details (dev only) */}
                            {isDev && this.state.error && (
                                <details className="mb-6">
                                    <summary className="flex items-center justify-between text-sm text-slate-500 cursor-pointer hover:text-slate-700 mb-2 font-semibold select-none">
                                        <span>Dettagli Tecnici (Dev Only)</span>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const text = `${this.state.error?.message}\n\n${this.state.error?.stack}`;
                                                navigator.clipboard.writeText(text);
                                                // Optional: Toast or small feedback
                                            }}
                                            className="p-1 hover:bg-slate-100 rounded-md transition-colors text-slate-400 hover:text-slate-600"
                                            title={this.props.t('errors.copy_error')}
                                        >
                                            <AppIcon name="copy" className="w-4 h-4" />
                                        </button>
                                    </summary>
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-xs font-mono text-red-900 overflow-auto max-h-40 relative">
                                        <div className="font-bold mb-2">{this.state.error.message}</div>
                                        <div className="text-red-700 whitespace-pre-wrap opacity-75">
                                            {this.state.error.stack}
                                        </div>
                                    </div>
                                </details>
                            )}

                            {/* Action buttons */}
                            <div className="space-y-3">
                                {config.actions.map((action, index) => (
                                    <button
                                        key={index}
                                        onClick={action.onClick}
                                        className={`w-full px-6 py-3 rounded-xl font-bold transition-all ${action.variant === 'primary'
                                            ? 'animated-gradient-button text-white shadow-lg shadow-sky-100 hover:shadow-xl'
                                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                            }`}
                                    >
                                        {action.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="bg-slate-50 px-8 py-4 text-center">
                            <p className="text-xs text-slate-500">
                                {this.props.t('errors.persist_support')}
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Wrapper to provide translation to class component
export function ErrorBoundaryWithTranslation(props: Omit<Props, 't'>) {
    const { t } = useTranslation();
    return <ErrorBoundary {...props} t={t} />;
}

// Export as default for convenience
export default ErrorBoundaryWithTranslation;
